import React from 'react';
import { Eye, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { IntegrationLogListItem, IntegrationLogTab, Pagination } from '../../../types';
import { LoadingSpinner } from '../../ui/Loading';
import {
  Button as SharedButton,
  DataTableShell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  NumberedPagination,
} from '@kioskgaming/ui';
import { cn } from '../../../utils';

interface IntegrationLogTableProps {
  tab: IntegrationLogTab;
  logs: IntegrationLogListItem[];
  pagination: Pagination;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onViewDetail: (log: IntegrationLogListItem) => void;
}

const IntegrationLogTable: React.FC<IntegrationLogTableProps> = ({
  tab,
  logs,
  pagination,
  isLoading,
  onPageChange,
  onViewDetail,
}) => {
  const fmt = useFormatDate();
  const isWebhook = tab === 'webhooks';

  return (
    <DataTableShell>
      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : logs.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No logs found for this range.</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                {!isWebhook && <TableHead>Provider</TableHead>}
                <TableHead>{isWebhook ? 'Status' : 'Method'}</TableHead>
                {!isWebhook && <TableHead>HTTP</TableHead>}
                {!isWebhook && <TableHead>Duration</TableHead>}
                {isWebhook && <TableHead>Transaction</TableHead>}
                {isWebhook && <TableHead>Processed</TableHead>}
                {!isWebhook && <TableHead>Result</TableHead>}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={String(log.id)}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {fmt(log.createdAt)}
                  </TableCell>
                  {!isWebhook && (
                    <TableCell className="text-sm font-medium">{log.providerCode || '—'}</TableCell>
                  )}
                  <TableCell className="text-sm">
                    {isWebhook ? log.status || '—' : log.apiMethod || '—'}
                  </TableCell>
                  {!isWebhook && (
                    <TableCell className="text-sm">{log.responseStatus ?? '—'}</TableCell>
                  )}
                  {!isWebhook && (
                    <TableCell className="text-sm">
                      {log.durationMs != null ? `${log.durationMs}ms` : '—'}
                    </TableCell>
                  )}
                  {isWebhook && (
                    <TableCell className="text-xs font-mono max-w-[140px] truncate">
                      {log.transactionId || '—'}
                    </TableCell>
                  )}
                  {isWebhook && (
                    <TableCell>
                      {log.processed ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                      )}
                    </TableCell>
                  )}
                  {!isWebhook && (
                    <TableCell>
                      {log.success ? (
                        <span className="inline-flex items-center text-green-700 text-xs font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-red-700 text-xs font-medium">
                          <AlertCircle className="h-3.5 w-3.5 mr-1" />
                          Fail
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <SharedButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetail(log)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </SharedButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div
            className={cn(
              'flex items-center justify-between border-t border-border px-4 py-3 text-sm',
            )}
          >
            <span className="text-muted-foreground">
              Page {pagination.page} of {pagination.pages || 1} ({pagination.total} total)
            </span>
            <NumberedPagination
              currentPage={pagination.page}
              totalPages={pagination.pages || 1}
              onPageChange={onPageChange}
              variant="admin"
            />
          </div>
        </>
      )}
    </DataTableShell>
  );
};

export default IntegrationLogTable;
